import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useRef } from 'preact/hooks';
const STATUS_MESSAGES = [
    'Fueling up',
    'Running diagnostics',
    'Calibrating thrusters',
    'Loading payload',
    'Checking star charts',
    'Warming up engines',
    'Consulting mission control',
    'Aligning trajectory',
    'Pressurizing fuel tanks',
    'Systems nominal',
];
/** Twinkling star */
function Star({ top, left, delay, size = 1 }) {
    return (_jsx("div", { className: "absolute rounded-full bg-white", style: {
            top,
            left,
            width: `${size}px`,
            height: `${size}px`,
            animation: `rocketTwinkle 3s ease-in-out ${delay}s infinite`,
        } }));
}
/** A single progress bar with JS-driven irregular fill */
function SystemBar({ label, seed, blastOff }) {
    const [percent, setPercent] = useState(0);
    useEffect(() => {
        // Snap to 100% and stop the tick loop on blast-off
        if (blastOff) {
            setPercent(100);
            return;
        }
        let current = 0;
        let timeout;
        const tick = () => {
            // Irregular increments: mostly small, occasionally bigger
            const roll = Math.random();
            let increment;
            if (roll < 0.5) {
                increment = 0.3 + Math.random() * 0.5; // tiny crawl
            }
            else if (roll < 0.8) {
                increment = 1 + Math.random() * 2; // small jump
            }
            else {
                increment = 3 + Math.random() * 5; // bigger burst
            }
            // Slow down as we get higher
            if (current > 70)
                increment *= 0.3;
            else if (current > 50)
                increment *= 0.5;
            current = Math.min(current + increment, 96);
            setPercent(Math.round(current));
            // Irregular timing: mostly slow, sometimes fast
            const delay = roll < 0.8
                ? 800 + Math.random() * 2000 // slow (0.8–2.8s)
                : 200 + Math.random() * 400; // fast burst (0.2–0.6s)
            if (current < 96) {
                timeout = setTimeout(tick, delay);
            }
        };
        // Stagger start per bar
        timeout = setTimeout(tick, seed * 600);
        return () => clearTimeout(timeout);
    }, [seed, blastOff]);
    return (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: "text-[10px] font-mono text-slate-500 w-10 text-right uppercase tracking-wider", children: label }), _jsxs("div", { className: "flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden relative", children: [_jsx("div", { className: "h-full rounded-full transition-all duration-300 ease-out", style: {
                            width: `${percent}%`,
                            background: 'linear-gradient(90deg, #52de45, #3ab832)',
                        } }), _jsx("div", { className: "absolute inset-0 pointer-events-none", style: {
                            background: 'linear-gradient(90deg, transparent 0%, rgba(82,222,69,0.3) 50%, transparent 100%)',
                            backgroundSize: '40% 100%',
                            animation: 'rocketBarCrawl 2s linear infinite',
                        } })] }), _jsxs("span", { className: "text-[10px] font-mono text-primary-500/70 w-8 tabular-nums", children: [percent, "%"] })] }));
}
/** SVG schematic-style rocket — larger version */
function RocketSVG({ glowing }) {
    const strokeColor = glowing ? '#52de45' : '#94a3b8';
    const accentColor = '#52de45';
    return (_jsxs("svg", { width: "80", height: "176", viewBox: "0 0 64 140", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M32 8 L42 38 L22 38 Z", stroke: strokeColor, strokeWidth: "1.5", fill: "none" }), _jsx("circle", { cx: "32", cy: "18", r: "1", fill: accentColor, opacity: glowing ? 1 : 0.5, children: glowing && _jsx("animate", { attributeName: "opacity", values: "1;0.3;1", dur: "2s", repeatCount: "indefinite" }) }), _jsx("rect", { x: "22", y: "38", width: "20", height: "60", rx: "2", stroke: strokeColor, strokeWidth: "1.5", fill: "none" }), _jsx("circle", { cx: "32", cy: "54", r: "6", stroke: accentColor, strokeWidth: "1", fill: "none" }), _jsx("circle", { cx: "32", cy: "54", r: "3", fill: accentColor, opacity: "0.15" }), _jsx("circle", { cx: "32", cy: "54", r: "1.5", fill: accentColor, opacity: glowing ? 0.6 : 0.3, children: glowing && _jsx("animate", { attributeName: "opacity", values: "0.6;0.2;0.6", dur: "1.5s", repeatCount: "indefinite" }) }), _jsx("line", { x1: "22", y1: "70", x2: "42", y2: "70", stroke: accentColor, strokeWidth: "1", opacity: "0.5" }), _jsx("line", { x1: "22", y1: "74", x2: "42", y2: "74", stroke: accentColor, strokeWidth: "1", opacity: "0.3" }), _jsx("line", { x1: "24", y1: "82", x2: "30", y2: "82", stroke: strokeColor, strokeWidth: "0.5", opacity: "0.4" }), _jsx("line", { x1: "34", y1: "82", x2: "40", y2: "82", stroke: strokeColor, strokeWidth: "0.5", opacity: "0.4" }), _jsx("line", { x1: "24", y1: "86", x2: "28", y2: "86", stroke: strokeColor, strokeWidth: "0.5", opacity: "0.4" }), _jsx("path", { d: "M22 88 L12 108 L22 98", stroke: strokeColor, strokeWidth: "1.5", fill: "none" }), _jsx("path", { d: "M42 88 L52 108 L42 98", stroke: strokeColor, strokeWidth: "1.5", fill: "none" }), _jsx("path", { d: "M24 98 L20 110 L44 110 L40 98", stroke: strokeColor, strokeWidth: "1.5", fill: "none" }), _jsx("ellipse", { cx: "32", cy: "110", rx: "8", ry: "2", stroke: strokeColor, strokeWidth: "1", fill: "none" }), _jsx("circle", { cx: "24", cy: "44", r: "1.5", fill: "#ef4444", children: _jsx("animate", { attributeName: "opacity", values: "1;0.2;1", dur: "1.2s", repeatCount: "indefinite" }) }), _jsx("circle", { cx: "40", cy: "44", r: "1.5", fill: accentColor, children: _jsx("animate", { attributeName: "opacity", values: "0.2;1;0.2", dur: "1.2s", repeatCount: "indefinite" }) })] }));
}
/** Flame SVG for blast-off */
function FlameSVG() {
    return (_jsxs("svg", { width: "80", height: "100", viewBox: "0 0 64 80", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { animation: 'rocketFlameFlicker 0.1s infinite alternate', marginTop: '-4px' }, children: [_jsx("path", { d: "M20 0 Q12 30, 18 50 Q24 65, 32 75 Q40 65, 46 50 Q52 30, 44 0", fill: "url(#flameGradient)", opacity: "0.7" }), _jsx("path", { d: "M24 0 Q20 20, 25 40 Q28 55, 32 62 Q36 55, 39 40 Q44 20, 40 0", fill: "url(#flameInner)", opacity: "0.9" }), _jsx("path", { d: "M28 0 Q26 15, 30 30 Q31 40, 32 45 Q33 40, 34 30 Q38 15, 36 0", fill: "#fff", opacity: "0.6" }), _jsxs("defs", { children: [_jsxs("linearGradient", { id: "flameGradient", x1: "32", y1: "0", x2: "32", y2: "75", gradientUnits: "userSpaceOnUse", children: [_jsx("stop", { offset: "0%", stopColor: "#fbbf24" }), _jsx("stop", { offset: "40%", stopColor: "#f97316" }), _jsx("stop", { offset: "100%", stopColor: "#ef4444", stopOpacity: "0" })] }), _jsxs("linearGradient", { id: "flameInner", x1: "32", y1: "0", x2: "32", y2: "62", gradientUnits: "userSpaceOnUse", children: [_jsx("stop", { offset: "0%", stopColor: "#fef3c7" }), _jsx("stop", { offset: "50%", stopColor: "#fbbf24" }), _jsx("stop", { offset: "100%", stopColor: "#f97316", stopOpacity: "0" })] })] })] }));
}
/** Launchpad — thin lines, gantry style */
function LaunchpadSVG() {
    return (_jsxs("svg", { width: "200", height: "30", viewBox: "0 0 160 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("line", { x1: "20", y1: "4", x2: "140", y2: "4", stroke: "#475569", strokeWidth: "2" }), _jsx("line", { x1: "40", y1: "4", x2: "30", y2: "22", stroke: "#334155", strokeWidth: "1" }), _jsx("line", { x1: "80", y1: "4", x2: "80", y2: "22", stroke: "#334155", strokeWidth: "1" }), _jsx("line", { x1: "120", y1: "4", x2: "130", y2: "22", stroke: "#334155", strokeWidth: "1" }), _jsx("line", { x1: "25", y1: "22", x2: "135", y2: "22", stroke: "#334155", strokeWidth: "1" }), _jsx("line", { x1: "55", y1: "4", x2: "60", y2: "-10", stroke: "#475569", strokeWidth: "1", strokeDasharray: "3 2" }), _jsx("line", { x1: "105", y1: "4", x2: "100", y2: "-10", stroke: "#475569", strokeWidth: "1", strokeDasharray: "3 2" })] }));
}
// Fixed star positions
const STARS = [
    { top: '6%', left: '12%', delay: 0, size: 1 },
    { top: '10%', left: '70%', delay: 1.2, size: 2 },
    { top: '20%', left: '86%', delay: 0.5, size: 1 },
    { top: '4%', left: '43%', delay: 2.1, size: 1 },
    { top: '28%', left: '6%', delay: 1.8, size: 2 },
    { top: '16%', left: '53%', delay: 0.3, size: 1 },
    { top: '33%', left: '90%', delay: 1.5, size: 1 },
    { top: '40%', left: '18%', delay: 0.8, size: 1 },
    { top: '13%', left: '33%', delay: 2.5, size: 2 },
    { top: '46%', left: '76%', delay: 1.0, size: 1 },
    { top: '53%', left: '10%', delay: 0.7, size: 1 },
    { top: '23%', left: '60%', delay: 1.9, size: 1 },
    { top: '36%', left: '40%', delay: 2.3, size: 2 },
    { top: '58%', left: '83%', delay: 0.2, size: 1 },
    { top: '50%', left: '48%', delay: 1.4, size: 1 },
];
const SYSTEM_BARS = [
    { label: 'FUEL', seed: 0 },
    { label: 'NAV', seed: 1 },
    { label: 'COMM', seed: 2 },
    { label: 'SYS', seed: 3 },
];
export function RocketLaunch({ phase, onComplete, blastOffDuration = 4500 }) {
    const [statusIndex, setStatusIndex] = useState(0);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;
    // Cycle status messages
    useEffect(() => {
        if (phase !== 'idle')
            return;
        const interval = setInterval(() => {
            setStatusIndex(i => (i + 1) % STATUS_MESSAGES.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [phase]);
    // Blast-off complete timer
    useEffect(() => {
        if (phase !== 'blasting-off')
            return;
        const timer = setTimeout(() => {
            onCompleteRef.current?.();
        }, blastOffDuration);
        return () => clearTimeout(timer);
    }, [phase, blastOffDuration]);
    const isBlastingOff = phase === 'blasting-off';
    return (_jsxs("div", { className: "h-full relative overflow-hidden bg-slate-950 flex flex-col items-center justify-center select-none", children: [_jsx("style", { children: `
                @keyframes rocketTwinkle {
                    0%, 100% { opacity: 0.2; }
                    50% { opacity: 0.8; }
                }
                @keyframes rocketBarCrawl {
                    0% { background-position: -40% 0; }
                    100% { background-position: 140% 0; }
                }
                @keyframes rocketLiftoff {
                    0% { transform: translateY(0); }
                    5% { transform: translateY(3px); }
                    15% { transform: translateY(-2vh); }
                    100% { transform: translateY(-140vh); }
                }
                @keyframes rocketShake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-1.5px); }
                    75% { transform: translateX(1.5px); }
                }
                @keyframes rocketFlameFlicker {
                    0% { transform: scaleY(1) scaleX(1); }
                    100% { transform: scaleY(1.1) scaleX(0.92); }
                }
                @keyframes rocketSmokeBurst {
                    0% { opacity: 0.6; transform: scale(1) translateY(0); }
                    100% { opacity: 0; transform: scale(8) translateY(-10px); }
                }
                @keyframes rocketFadeOut {
                    0% { opacity: 0; }
                    100% { opacity: 1; }
                }
                @keyframes rocketStatusFade {
                    0% { opacity: 0; transform: translateY(4px); }
                    12% { opacity: 1; transform: translateY(0); }
                    88% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-4px); }
                }
                @keyframes rocketGlow {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 0.6; }
                }
                @keyframes rocketGridPulse {
                    0%, 100% { opacity: 0.03; }
                    50% { opacity: 0.06; }
                }
            ` }), _jsx("div", { className: "absolute inset-0 pointer-events-none", style: {
                    backgroundImage: 'linear-gradient(rgba(82,222,69,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(82,222,69,0.04) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    animation: 'rocketGridPulse 6s ease-in-out infinite',
                } }), STARS.map((star, i) => (_jsx(Star, { top: star.top, left: star.left, delay: star.delay, size: star.size }, i))), _jsxs("div", { className: "relative flex flex-col items-center", style: isBlastingOff ? { animation: 'rocketShake 0.06s linear 0s 12' } : undefined, children: [_jsxs("div", { className: "flex flex-col items-center relative", style: isBlastingOff ? { animation: 'rocketLiftoff 3.5s cubic-bezier(0.25, 0, 0.3, 1) 0.5s forwards' } : undefined, children: [_jsx("div", { className: "absolute -inset-10 rounded-full pointer-events-none", style: {
                                    background: 'radial-gradient(circle, rgba(82,222,69,0.08) 0%, transparent 70%)',
                                    animation: 'rocketGlow 3s ease-in-out infinite',
                                } }), _jsx(RocketSVG, { glowing: !isBlastingOff }), isBlastingOff && _jsx(FlameSVG, {})] }), isBlastingOff && (_jsx("div", { className: "absolute rounded-full", style: {
                            bottom: '12px',
                            width: '14px',
                            height: '14px',
                            background: 'radial-gradient(circle, rgba(148,163,184,0.5), transparent)',
                            animation: 'rocketSmokeBurst 1.8s ease-out 0.4s forwards',
                        } })), _jsx("div", { className: "mt-[-2px]", children: _jsx(LaunchpadSVG, {}) })] }), _jsx("div", { className: "mt-8 w-56 space-y-2 transition-opacity duration-500", style: isBlastingOff ? { opacity: 0, transitionDelay: '0.6s' } : undefined, children: SYSTEM_BARS.map((bar) => (_jsx(SystemBar, { label: bar.label, seed: bar.seed, blastOff: isBlastingOff }, bar.label))) }), !isBlastingOff && (_jsx("div", { className: "mt-6 text-center", children: _jsxs("span", { className: "text-xs font-mono text-primary-500/60 tracking-widest uppercase", style: { animation: 'rocketStatusFade 4s ease-in-out forwards' }, children: [STATUS_MESSAGES[statusIndex], _jsx("span", { className: "animate-pulse ml-0.5", children: "_" })] }, statusIndex) })), isBlastingOff && (_jsx("div", { className: "absolute inset-0 bg-slate-950 pointer-events-none", style: { opacity: 0, animation: 'rocketFadeOut 1.5s ease-in 2.5s forwards' } }))] }));
}
